import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { missingField } from 'app/constants';

/** MUI Tooltip styling aligned with TasksSearchWrapper / global task search grid */
export const taskSearchGridWhiteTooltipProps = {
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: '#fff',
        color: '#333333',
        border: '1px solid rgba(224, 224, 224, 1)',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        fontSize: '12px',
      },
    },
    arrow: { sx: { color: '#fff' } },
  },
};

/** Normalize legacy em-dash empty markers to the app-standard missing field token */
export function normalizeTaskGridCellDisplay(v: unknown): string {
  if (v == null || v === '') return missingField;
  if (v === '—') return missingField;
  return String(v);
}

export const OverflowTooltipWrapper = ({
  tooltipText,
  children,
  sx: boxSx,
  onClick,
}: {
  tooltipText: string;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
  onClick?: (e: React.MouseEvent) => void;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const nestedSpan = el.querySelector<HTMLElement>('.MuiDataGrid-treeDataGroupingCell > span');
    if (nestedSpan) {
      setIsOverflowing(nestedSpan.scrollWidth > nestedSpan.clientWidth);
      return;
    }
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, []);

  React.useEffect(() => {
    checkOverflow();
  }, [tooltipText, checkOverflow]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  return (
    <Tooltip title={tooltipText} arrow disableHoverListener={!isOverflowing} {...taskSearchGridWhiteTooltipProps}>
      <Box
        ref={ref}
        sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...boxSx }}
        onClick={onClick}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export const OverflowTooltipCell = ({ value }: { value: string }) => (
  <OverflowTooltipWrapper tooltipText={value || ''}>{value}</OverflowTooltipWrapper>
);
